import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useData } from '../context/DataContext';
import toast from 'react-hot-toast';

const { FiPlus, FiEdit, FiTrash2, FiEye, FiEyeOff, FiSearch, FiFilter, FiCalendar, FiDollarSign } = FiIcons;

const VehicleManagement = () => {
  const { vehicles, toggleVehicleStatus, deleteVehicle, loading } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  const filteredVehicles = vehicles
    .filter(vehicle => {
      const matchesSearch = searchTerm === '' || 
        `${vehicle.brand} ${vehicle.model} ${vehicle.version}`.toLowerCase()
        .includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || vehicle.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'price-asc': return a.price - b.price;
        case 'price-desc': return b.price - a.price;
        case 'year-desc': return b.year - a.year;
        case 'year-asc': return a.year - b.year;
        case 'oldest': return new Date(a.created_at) - new Date(b.created_at);
        default: // newest
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

  const handleToggleStatus = (id) => {
    toggleVehicleStatus(id);
    toast.success('Estado del vehículo actualizado');
  };

  const handleDelete = (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este vehículo?')) {
      deleteVehicle(id);
      toast.success('Vehículo eliminado correctamente');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatKilometers = (km) => {
    return new Intl.NumberFormat('es-ES').format(km) + ' km';
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#2c3e50]">Gestión de Vehículos</h1>
          <p className="text-gray-600 mt-2">Total: {filteredVehicles.length} vehículos</p>
        </div>
        <Link 
          to="/admin/vehiculos/nuevo" 
          className="bg-[#e67e22] hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center space-x-2"
        >
          <SafeIcon icon={FiPlus} className="w-5 h-5" />
          <span>Añadir Vehículo</span>
        </Link>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar vehículos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
          >
            <option value="newest">Más Recientes</option>
            <option value="oldest">Más Antiguos</option>
            <option value="price-asc">Precio: Menor a Mayor</option>
            <option value="price-desc">Precio: Mayor a Menor</option>
            <option value="year-desc">Año: Más Nuevo</option>
            <option value="year-asc">Año: Más Antiguo</option>
          </select>

          {/* Clear Filters */}
          <button 
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
              setSortBy('newest');
            }}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
          >
            Limpiar Filtros
          </button>
        </div>
      </div>

      {/* Vehicles Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-64 bg-white rounded-lg shadow-md p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#005c91]"></div>
            <span className="ml-4 text-gray-600 font-medium">Cargando inventario...</span>
        </div>
      ) : filteredVehicles.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <div className="text-gray-400 mb-4">
            <SafeIcon icon={FiSearch} className="w-16 h-16 mx-auto" />
          </div>
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No se encontraron vehículos</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || statusFilter !== 'all' 
              ? 'Intenta ajustar tus filtros de búsqueda' 
              : 'Aún no has añadido ningún vehículo'}
          </p>
          <Link 
            to="/admin/vehiculos/nuevo" 
            className="bg-[#005c91] hover:bg-[#3498db] text-white px-6 py-3 rounded-lg font-semibold transition-colors inline-flex items-center space-x-2"
          >
            <SafeIcon icon={FiPlus} className="w-5 h-5" />
            <span>Añadir Primer Vehículo</span>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredVehicles.map((vehicle, index) => (
            <motion.div 
              key={vehicle.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="relative">
                <img 
                  src={vehicle.images[0] || 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=400&h=250&fit=crop'} 
                  alt={`${vehicle.brand} ${vehicle.model}`} 
                  className="w-full h-48 object-cover" 
                />
                <div className="absolute top-4 left-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${vehicle.status === 'active' ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}`}>
                    {vehicle.status === 'active' ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="bg-[#e67e22] text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {vehicle.year}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-[#2c3e50] mb-2">
                  {vehicle.brand} {vehicle.model}
                </h3>
                <p className="text-gray-600 mb-4">{vehicle.version}</p>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-[#7f8c8d]" />
                    <span className="text-sm font-semibold text-[#27ae60]">
                      {formatPrice(vehicle.price)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4 text-[#7f8c8d]" />
                    <span className="text-sm text-gray-600">{vehicle.year}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-sm text-gray-600">
                      {formatKilometers(vehicle.kilometers)} • {vehicle.fuel} • {vehicle.transmission}
                    </span>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <Link 
                    to={`/admin/vehiculos/editar/${vehicle.id}`} 
                    className="flex-1 bg-[#3498db] hover:bg-blue-600 text-white py-2 px-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiEdit} className="w-4 h-4" />
                    <span>Editar</span>
                  </Link>
                  <button 
                    onClick={() => handleToggleStatus(vehicle.id)}
                    className={`flex-1 py-2 px-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${vehicle.status === 'active' ? 'bg-gray-500 hover:bg-gray-600 text-white' : 'bg-[#27ae60] hover:bg-green-600 text-white'}`}
                  >
                    <SafeIcon icon={vehicle.status === 'active' ? FiEyeOff : FiEye} className="w-4 h-4" />
                    <span>{vehicle.status === 'active' ? 'Ocultar' : 'Mostrar'}</span>
                  </button>
                  <button 
                    onClick={() => handleDelete(vehicle.id)}
                    className="bg-[#e74c3c] hover:bg-red-600 text-white p-2 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VehicleManagement;